import { useState, useEffect, useCallback, useRef } from 'react'
import { doc, onSnapshot, Unsubscribe } from 'firebase/firestore'
import { db } from '../firebase'
import {
  TeacherProfile,
  TeacherProfileUpdate,
  getTeacherProfile,
  updateTeacherProfile,
  uploadProfilePhoto,
  uploadBannerImage,
  TeacherProfileError,
} from '../services/teacherProfileService'
// ============================================================================
// CACHE CONFIGURATION
// ============================================================================
const CACHE_VALIDITY_MS = 5 * 60 * 1000 // 5 minutes
interface CachedProfile {
  data: TeacherProfile
  timestamp: number
}
// Global cache shared across all hook instances
const profileCache = new Map<string, CachedProfile>()
// Helper to check if cache is valid
const isCacheValid = (cached: CachedProfile | undefined): boolean => {
  if (!cached) return false
  const now = Date.now()
  return now - cached.timestamp < CACHE_VALIDITY_MS
}
// Helper to get cached profile
const getCachedProfile = (teacherId: string): TeacherProfile | null => {
  const cached = profileCache.get(teacherId)
  if (isCacheValid(cached)) {
    console.log(`[useTeacherProfile] Cache HIT for ${teacherId}`)
    return cached!.data
  }
  console.log(`[useTeacherProfile] Cache MISS for ${teacherId}`)
  return null
}
// Helper to set cached profile
const setCachedProfile = (teacherId: string, profile: TeacherProfile): void => {
  profileCache.set(teacherId, {
    data: profile,
    timestamp: Date.now(),
  })
  console.log(`[useTeacherProfile] Cache SET for ${teacherId}`)
}
// Helper to invalidate cache
const invalidateCache = (teacherId: string): void => {
  profileCache.delete(teacherId)
  console.log(`[useTeacherProfile] Cache INVALIDATED for ${teacherId}`)
}
// ============================================================================
// HOOK TYPES (UPDATED)
// ============================================================================
interface UseTeacherProfileReturn {
  profile: TeacherProfile | null
  loading: boolean
  error: Error | null
  updateProfile: (updates: TeacherProfileUpdate) => Promise<void>
  uploadPhoto: (
    file: File,
    onProgress?: (progress: number) => void,
  ) => Promise<string>
  uploadBanner: (
    file: File,
    onProgress?: (progress: number) => void,
  ) => Promise<string>
  refetch: () => Promise<void>
}
// ============================================================================
// MAIN HOOK
// ============================================================================
export const useTeacherProfile = (
  teacherId: string | undefined | null,
): UseTeacherProfileReturn => {
  // State management
  const [profile, setProfile] = useState<TeacherProfile | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  // Refs for cleanup and tracking
  const unsubscribeRef = useRef<Unsubscribe | null>(null)
  const isMountedRef = useRef<boolean>(true)
  // ============================================================================
  // FETCH PROFILE (with caching)
  // ============================================================================
  const fetchProfile = useCallback(async () => {
    if (!teacherId) {
      setProfile(null)
      setLoading(false)
      setError(null)
      return
    }
    try {
      setLoading(true)
      setError(null)
      // Check cache first
      const cachedProfile = getCachedProfile(teacherId)
      if (cachedProfile) {
        setProfile(cachedProfile)
        setLoading(false)
        return
      }
      // Fetch from Firestore
      console.log(`[useTeacherProfile] Fetching profile for ${teacherId}`)
      const fetchedProfile = await getTeacherProfile(teacherId)
      if (isMountedRef.current) {
        setProfile(fetchedProfile)
        setCachedProfile(teacherId, fetchedProfile)
        setLoading(false)
      }
    } catch (err: any) {
      console.error('[useTeacherProfile] Error fetching profile:', err)
      if (isMountedRef.current) {
        setError(err)
        setLoading(false)
      }
    }
  }, [teacherId])
  // ============================================================================
  // REAL-TIME SUBSCRIPTION
  // ============================================================================
  useEffect(() => {
    if (!teacherId) {
      setProfile(null)
      setLoading(false)
      setError(null)
      return
    }
    // Check cache first for immediate display
    const cachedProfile = getCachedProfile(teacherId)
    if (cachedProfile) {
      setProfile(cachedProfile)
      setLoading(false)
    } else {
      setLoading(true)
    }
    // Set up real-time subscription
    console.log(
      `[useTeacherProfile] Setting up real-time listener for ${teacherId}`,
    )
    const teacherRef = doc(db, 'teachers', teacherId)
    const unsubscribe = onSnapshot(
      teacherRef,
      (snapshot) => {
        if (!isMountedRef.current) return
        if (snapshot.exists()) {
          const data = snapshot.data() as TeacherProfile
          const profileData: TeacherProfile = {
            uid: snapshot.id,
            ...data,
          }
          console.log(
            `[useTeacherProfile] Real-time update received for ${teacherId}`,
          )
          setProfile(profileData)
          setCachedProfile(teacherId, profileData)
          setError(null)
        } else {
          console.warn(`[useTeacherProfile] Profile not found for ${teacherId}`)
          setProfile(null)
          setError(new TeacherProfileError('Profile not found', 'not-found'))
        }
        setLoading(false)
      },
      (err) => {
        console.error('[useTeacherProfile] Snapshot error:', err)
        if (isMountedRef.current) {
          setError(err)
          setLoading(false)
        }
      },
    )
    unsubscribeRef.current = unsubscribe
    // Cleanup function
    return () => {
      console.log(`[useTeacherProfile] Cleaning up listener for ${teacherId}`)
      unsubscribe()
      unsubscribeRef.current = null
    }
  }, [teacherId])
  // ============================================================================
  // UPDATE PROFILE (with optimistic updates)
  // ============================================================================
  const updateProfileHandler = useCallback(
    async (updates: TeacherProfileUpdate): Promise<void> => {
      if (!teacherId) {
        throw new TeacherProfileError(
          'Cannot update profile: teacherId is missing',
          'invalid-teacher-id',
        )
      }
      if (!profile) {
        throw new TeacherProfileError(
          'Cannot update profile: profile not loaded',
          'profile-not-loaded',
        )
      }
      // Store previous state for rollback
      const previousProfile = { ...profile }
      try {
        // Optimistic update - update UI immediately
        console.log('[useTeacherProfile] Applying optimistic update')
        const optimisticProfile: TeacherProfile = {
          ...profile,
          ...updates,
          updatedAt: new Date().toISOString(), // Temporary timestamp
        }
        setProfile(optimisticProfile)
        setCachedProfile(teacherId, optimisticProfile)
        // Perform actual update
        console.log('[useTeacherProfile] Updating profile in Firestore')
        await updateTeacherProfile(teacherId, updates)
        // Success - the onSnapshot listener will update with server data
        console.log('[useTeacherProfile] Profile update successful')
      } catch (err: any) {
        console.error('[useTeacherProfile] Profile update failed:', err)
        // Rollback optimistic update on error
        console.log('[useTeacherProfile] Rolling back optimistic update')
        setProfile(previousProfile)
        setCachedProfile(teacherId, previousProfile)
        // Re-throw error for caller to handle
        throw err
      }
    },
    [teacherId, profile],
  )
  // ============================================================================
  // UPLOAD PROFILE PHOTO (UPDATED with progress tracking)
  // ============================================================================
  const uploadPhotoHandler = useCallback(
    async (
      file: File,
      onProgress?: (progress: number) => void,
    ): Promise<string> => {
      if (!teacherId) {
        throw new TeacherProfileError(
          'Cannot upload photo: teacherId is missing',
          'invalid-teacher-id',
        )
      }
      if (!profile) {
        throw new TeacherProfileError(
          'Cannot upload photo: profile not loaded',
          'profile-not-loaded',
        )
      }
      // Store previous state for rollback
      const previousProfile = { ...profile }
      const previousPhotoUrl = profile.profilePictureUrl
      try {
        // Create temporary URL for immediate display
        const tempUrl = URL.createObjectURL(file)
        console.log('[useTeacherProfile] Applying optimistic photo update')
        const optimisticProfile: TeacherProfile = {
          ...profile,
          profilePictureUrl: tempUrl,
        }
        setProfile(optimisticProfile)
        // Perform actual upload with progress tracking
        console.log('[useTeacherProfile] Uploading profile photo')
        const downloadURL = await uploadProfilePhoto(
          teacherId,
          file,
          onProgress,
        )
        // Revoke temporary URL
        URL.revokeObjectURL(tempUrl)
        // Update with real URL (also updates Firestore via service)
        console.log('[useTeacherProfile] Profile photo upload successful')
        // The onSnapshot listener will update with the server data
        // But we can update immediately for better UX
        const updatedProfile: TeacherProfile = {
          ...profile,
          profilePictureUrl: downloadURL,
        }
        setProfile(updatedProfile)
        setCachedProfile(teacherId, updatedProfile)
        return downloadURL
      } catch (err: any) {
        console.error('[useTeacherProfile] Photo upload failed:', err)
        // Rollback optimistic update on error
        console.log('[useTeacherProfile] Rolling back optimistic photo update')
        setProfile(previousProfile)
        setCachedProfile(teacherId, previousProfile)
        // Re-throw error for caller to handle
        throw err
      }
    },
    [teacherId, profile],
  )
  // ============================================================================
  // UPLOAD BANNER IMAGE (UPDATED with progress tracking)
  // ============================================================================
  const uploadBannerHandler = useCallback(
    async (
      file: File,
      onProgress?: (progress: number) => void,
    ): Promise<string> => {
      if (!teacherId) {
        throw new TeacherProfileError(
          'Cannot upload banner: teacherId is missing',
          'invalid-teacher-id',
        )
      }
      if (!profile) {
        throw new TeacherProfileError(
          'Cannot upload banner: profile not loaded',
          'profile-not-loaded',
        )
      }
      // Store previous state for rollback
      const previousProfile = { ...profile }
      const previousBannerUrl = profile.bannerImageUrl
      try {
        // Create temporary URL for immediate display
        const tempUrl = URL.createObjectURL(file)
        console.log('[useTeacherProfile] Applying optimistic banner update')
        const optimisticProfile: TeacherProfile = {
          ...profile,
          bannerImageUrl: tempUrl,
        }
        setProfile(optimisticProfile)
        // Perform actual upload with progress tracking
        console.log('[useTeacherProfile] Uploading banner image')
        const downloadURL = await uploadBannerImage(teacherId, file, onProgress)
        // Revoke temporary URL
        URL.revokeObjectURL(tempUrl)
        // Update with real URL (also updates Firestore via service)
        console.log('[useTeacherProfile] Banner image upload successful')
        // The onSnapshot listener will update with the server data
        // But we can update immediately for better UX
        const updatedProfile: TeacherProfile = {
          ...profile,
          bannerImageUrl: downloadURL,
        }
        setProfile(updatedProfile)
        setCachedProfile(teacherId, updatedProfile)
        return downloadURL
      } catch (err: any) {
        console.error('[useTeacherProfile] Banner upload failed:', err)
        // Rollback optimistic update on error
        console.log('[useTeacherProfile] Rolling back optimistic banner update')
        setProfile(previousProfile)
        setCachedProfile(teacherId, previousProfile)
        // Re-throw error for caller to handle
        throw err
      }
    },
    [teacherId, profile],
  )
  // ============================================================================
  // MANUAL REFETCH
  // ============================================================================
  const refetch = useCallback(async (): Promise<void> => {
    if (!teacherId) {
      console.warn('[useTeacherProfile] Cannot refetch: teacherId is missing')
      return
    }
    console.log(`[useTeacherProfile] Manual refetch requested for ${teacherId}`)
    // Invalidate cache to force fresh fetch
    invalidateCache(teacherId)
    // Fetch fresh data
    await fetchProfile()
  }, [teacherId, fetchProfile])
  // ============================================================================
  // CLEANUP ON UNMOUNT
  // ============================================================================
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      console.log('[useTeacherProfile] Component unmounting, cleaning up')
      isMountedRef.current = false
      // Unsubscribe from real-time listener
      if (unsubscribeRef.current) {
        unsubscribeRef.current()
        unsubscribeRef.current = null
      }
    }
  }, [])
  // ============================================================================
  // RETURN HOOK API
  // ============================================================================
  return {
    profile,
    loading,
    error,
    updateProfile: updateProfileHandler,
    uploadPhoto: uploadPhotoHandler,
    uploadBanner: uploadBannerHandler,
    refetch,
  }
}
// ============================================================================
// UTILITY FUNCTIONS (exported for testing/debugging)
// ============================================================================
/**
 * Clear all cached profiles
 * Useful for testing or when user logs out
 */
export const clearProfileCache = (): void => {
  profileCache.clear()
  console.log('[useTeacherProfile] All cache cleared')
}
/**
 * Clear cache for specific teacher
 * @param teacherId - The teacher's unique ID
 */
export const clearTeacherCache = (teacherId: string): void => {
  invalidateCache(teacherId)
}
/**
 * Get cache statistics
 * Useful for debugging and monitoring
 */
export const getCacheStats = () => {
  const now = Date.now()
  const stats = {
    totalEntries: profileCache.size,
    validEntries: 0,
    expiredEntries: 0,
  }
  profileCache.forEach((cached) => {
    if (now - cached.timestamp < CACHE_VALIDITY_MS) {
      stats.validEntries++
    } else {
      stats.expiredEntries++
    }
  })
  return stats
}
